import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exportToExcel, exportToPDF } from '../lib/exportUtils';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { clientsAPI } from '../lib/api';
import { formatCurrency, formatDate, getInitials, cn, getErrorMessage, cleanPayload } from '../lib/utils';
import { filterClients } from '../lib/clientSearch';
import useDebounce from '../hooks/useDebounce';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Search, 
  Plus, 
  User, 
  Phone, 
  Mail, 
  Calendar,
  Gift,
  History,
  Edit,
  Trash2,
  Star,
  Tag,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Clock,
  ShoppingBag,
  Scissors,
  Beaker,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientesPage() {
  const { selectedBusiness } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced search term — the raw input keeps the caret snappy, the filter
  // only re-runs after 300 ms of quiet.
  const debouncedSearch = useDebounce(searchTerm, 300);
  // Client-side filtered list. Using useMemo would work too, but we prefer
  // a plain computation because the base list rarely changes and the filter
  // is cheap (a few thousand rows, string.includes).
  const filteredClients = filterClients(clients, debouncedSearch);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [clientHistory, setClientHistory] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    birthday: '',
    notes: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Audit modal (per-client full appointment audit trail)
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditClient, setAuditClient] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilterAppt, setAuditFilterAppt] = useState(null);
  const defaultAuditFrom = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  };
  const [auditFrom, setAuditFrom] = useState(defaultAuditFrom());
  const [auditTo, setAuditTo] = useState('');

  const loadAudit = useCallback(async (clientId, appointmentId) => {
    setAuditLoading(true);
    try {
      const params = {};
      if (auditFrom) params.date_from = auditFrom;
      if (auditTo) params.date_to = auditTo;
      if (appointmentId) params.appointment_id = appointmentId;
      const { data } = await clientsAPI.getAppointmentHistory(clientId, params);
      setAuditEvents(data.events || []);
    } catch (err) {
      toast.error('No se pudo cargar el historial');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFrom, auditTo]);

  const openAuditModal = (client, appointmentId) => {
    setAuditClient(client);
    setAuditFilterAppt(appointmentId || null);
    setAuditEvents([]);
    setAuditOpen(true);
  };

  useEffect(() => {
    if (auditOpen && auditClient) {
      loadAudit(auditClient.id, auditFilterAppt);
    }
  }, [auditOpen, auditClient, auditFilterAppt, auditFrom, auditTo, loadAudit]);

  const loadClients = useCallback(async () => {
    if (!selectedBusiness) return;

    try {
      setLoading(true);
      // Load the FULL list once and filter client-side. This avoids a network
      // round-trip on every keystroke and keeps the input snappy.
      const response = await clientsAPI.getAll({ limit: 5000 });
      setClients(response.data);
    } catch (error) {
      logger.error('Error loading clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Deep-link: /clientes?focus={client_id} opens the detail dialog directly
  // (used by the Top Clientes report "Ver historial" button).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || !clients.length) return;
    const target = clients.find((c) => c.id === focus);
    if (!target) return;
    openClientDetail(target);
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const openNewClient = () => {
    setSelectedClient(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      birthday: '',
      notes: '',
      tags: []
    });
    setDialogOpen(true);
  };

  const openEditClient = (client) => {
    setSelectedClient(client);
    setFormData({
      first_name: client.first_name,
      last_name: client.last_name,
      email: client.email || '',
      phone: client.phone || '',
      birthday: client.birthday || '',
      notes: client.notes || '',
      tags: client.tags || []
    });
    setDialogOpen(true);
  };

  const toggleClientHistory = async (client) => {
    if (expandedClient === client.id) {
      setExpandedClient(null);
      setClientHistory(null);
      return;
    }
    
    setExpandedClient(client.id);
    setLoadingHistory(true);
    
    try {
      const response = await clientsAPI.getHistory(client.id);
      setClientHistory(response.data);
    } catch (error) {
      logger.error('Error loading history:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openClientDetail = async (client) => {
    setSelectedClient(client);
    setDetailOpen(true);
    
    try {
      const response = await clientsAPI.getHistory(client.id);
      setClientHistory(response.data);
    } catch (error) {
      logger.error('Error loading history:', error);
    }
  };

  const handleSubmit = async () => {
    const first_name = (formData.first_name || '').trim();
    const last_name = (formData.last_name || '').trim();
    if (!first_name || !last_name) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }

    // Strip empty string optional fields so Pydantic EmailStr/etc don't 422
    const cleaned = cleanPayload(
      { ...formData, first_name, last_name },
      { keepEmptyArrays: true } // preserve tags: []
    );

    try {
      if (selectedClient) {
        await clientsAPI.update(selectedClient.id, cleaned);
        toast.success('Cliente actualizado');
      } else {
        await clientsAPI.create({
          ...cleaned,
          business_id: selectedBusiness.id
        });
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      loadClients();
    } catch (error) {
      logger.error('Error saving client:', error);
      toast.error(getErrorMessage(error, 'Error al guardar'));
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    
    try {
      await clientsAPI.delete(client.id);
      toast.success('Cliente eliminado');
      loadClients();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const addTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const getLoyaltyLevel = (points) => {
    if (points >= 500) return { label: 'Oro', color: 'text-amber-400 border-amber-500/30' };
    if (points >= 200) return { label: 'Plata', color: 'text-slate-500 border-slate-300' };
    return { label: 'Bronce', color: 'text-orange-400 border-orange-500/30' };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clientes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Clientes
          </h1>
          <p className="text-slate-500 mt-1">
            {clients.length} clientes registrados
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            onClick={() => {
              const rows = clients.map((c) => ({
                nombre: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                telefono: c.phone || '',
                correo: c.email || '',
                visitas: c.total_visits || 0,
                gastado: Number(c.total_spent || 0).toFixed(2),
                cumpleaños: c.birthday || '',
                ultima_visita: c.last_visit || '',
              }));
              exportToExcel(`Clientes_${selectedBusiness?.slug || 'export'}`, [{
                name: 'Clientes',
                columns: [
                  { header: 'Nombre', key: 'nombre' },
                  { header: 'Teléfono', key: 'telefono' },
                  { header: 'Correo', key: 'correo' },
                  { header: 'Visitas', key: 'visitas' },
                  { header: 'Total gastado', key: 'gastado' },
                  { header: 'Cumpleaños', key: 'cumpleaños' },
                  { header: 'Última visita', key: 'ultima_visita' },
                ],
                rows,
              }]);
              toast.success('Excel descargado');
            }}
            data-testid="export-clients-excel"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50"
            onClick={() => {
              const rows = clients.map((c) => ({
                nombre: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                telefono: c.phone || '',
                correo: c.email || '',
                visitas: String(c.total_visits || 0),
                gastado: Number(c.total_spent || 0).toFixed(2),
              }));
              exportToPDF(`Clientes_${selectedBusiness?.slug || 'export'}`, {
                businessName: selectedBusiness?.name,
                title: 'Base de datos de clientes',
                subtitle: `${clients.length} clientes registrados`,
                sections: [{
                  title: 'Listado',
                  columns: [
                    { header: 'Nombre', key: 'nombre' },
                    { header: 'Teléfono', key: 'telefono' },
                    { header: 'Correo', key: 'correo' },
                    { header: 'Visitas', key: 'visitas' },
                    { header: 'Gastado', key: 'gastado' },
                  ],
                  rows,
                }],
              });
              toast.success('PDF descargado');
            }}
            data-testid="export-clients-pdf"
          >
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button 
            onClick={openNewClient}
            className="bg-primary text-white hover:bg-primary/90"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo cliente
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre, teléfono o correo..."
          className="pl-10 bg-white border-slate-200"
          data-testid="search-clients"
        />
      </div>

      {/* Clients Grid/Table */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <User className="w-12 h-12 mb-3 opacity-50" />
              <p>
                {clients.length === 0
                  ? 'No hay clientes registrados'
                  : `Sin resultados para "${searchTerm}"`}
              </p>
              {clients.length === 0 && (
                <Button variant="link" onClick={openNewClient} className="mt-2">
                  Agregar primer cliente
                </Button>
              )}
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Visitas</TableHead>
                  <TableHead>Gastado</TableHead>
                  <TableHead>Puntos</TableHead>
                  <TableHead>Última visita</TableHead>
                  <TableHead className="w-20"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client, index) => {
                  const level = getLoyaltyLevel(client.loyalty_points);
                  const isExpanded = expandedClient === client.id;
                  return (
                    <React.Fragment key={client.id}>
                      <TableRow 
                        className="cursor-pointer animate-fade-in"
                        style={{ animationDelay: `${index * 0.03}s` }}
                        onClick={() => toggleClientHistory(client)}
                      >
                        <TableCell className="w-10">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleClientHistory(client);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-slate-200 text-white">
                                {getInitials(client.first_name, client.last_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{client.first_name} {client.last_name}</p>
                              {client.tags.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {client.tags.slice(0, 2).map(tag => (
                                    <Badge key={tag} variant="outline" className="text-[10px] px-1 text-slate-500">
                                      {tag}
                                    </Badge>
                                  ))}
                                  {client.tags.length > 2 && (
                                    <Badge variant="outline" className="text-[10px] px-1 text-slate-500">
                                      +{client.tags.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {client.phone && (
                            <p className="flex items-center gap-1 text-slate-500">
                              <Phone className="w-3 h-3" /> {client.phone}
                            </p>
                          )}
                          {client.email && (
                            <p className="flex items-center gap-1 text-slate-500 truncate max-w-[150px]">
                              <Mail className="w-3 h-3" /> {client.email}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{client.total_visits}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{formatCurrency(client.total_spent)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-amber-400" />
                          <span>{client.loyalty_points}</span>
                          <Badge variant="outline" className={cn("text-[10px]", level.color)}>
                            {level.label}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-500 text-sm">
                          {client.last_visit ? formatDate(client.last_visit) : 'Nunca'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 hover:bg-slate-100"
                            onClick={() => openEditClient(client)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 hover:bg-slate-100 hover:text-red-400"
                            onClick={() => handleDelete(client)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                      </TableRow>
                      
                      {/* Expanded History Row */}
                      {isExpanded && (
                        <TableRow key={`${client.id}-history`} className="bg-white">
                          <TableCell colSpan={8} className="p-0">
                            <div className="p-4 border-t border-slate-200 animate-fade-in">
                              {loadingHistory ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                                </div>
                              ) : clientHistory ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Appointments History */}
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium flex items-center gap-2 text-slate-500">
                                      <Scissors className="w-4 h-4" />
                                      Últimas citas ({clientHistory.appointments?.length || 0})
                                    </h4>
                                    <ScrollArea className="h-32">
                                      {clientHistory.appointments?.length > 0 ? (
                                        <div className="space-y-2 pr-4">
                                          {clientHistory.appointments.slice(0, 5).map((apt, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                                              <div>
                                                <p className="text-slate-500">{apt.service_names?.join(', ') || 'Servicio'}</p>
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openAuditModal(client, apt.id);
                                                  }}
                                                  className="text-xs text-slate-500 hover:text-emerald-600 hover:underline transition"
                                                  data-testid={`audit-link-${apt.id}`}
                                                >
                                                  {formatDate(apt.date)} • {apt.barber_name}
                                                </button>
                                              </div>
                                              <Badge variant="outline" className={cn(
                                                "text-[10px]",
                                                apt.status === 'completed' ? "text-emerald-400 border-emerald-500/30" : "text-slate-500"
                                              )}>
                                                {apt.status === 'completed' ? 'Completada' : apt.status}
                                              </Badge>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-500 py-2">Sin citas registradas</p>
                                      )}
                                    </ScrollArea>
                                  </div>
                                  
                                  {/* Sales History */}
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium flex items-center gap-2 text-slate-500">
                                      <ShoppingBag className="w-4 h-4" />
                                      Últimas compras ({clientHistory.sales?.length || 0})
                                    </h4>
                                    <ScrollArea className="h-32">
                                      {clientHistory.sales?.length > 0 ? (
                                        <div className="space-y-2 pr-4">
                                          {clientHistory.sales.slice(0, 5).map((sale, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                                              <div>
                                                <p className="text-slate-500">{sale.items?.length || 0} productos</p>
                                                <p className="text-xs text-slate-500">
                                                  {formatDate(sale.created_at)}
                                                  <span
                                                    className={`ml-2 text-[9px] px-1 py-0 rounded border font-medium ${
                                                      sale.appointment_id
                                                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                                        : 'bg-amber-100 text-amber-800 border-amber-200'
                                                    }`}
                                                  >
                                                    {sale.appointment_id ? 'Con cita' : 'Walk-in'}
                                                  </span>
                                                </p>
                                              </div>
                                              <span className="font-medium text-emerald-400">
                                                {formatCurrency(sale.total)}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-500 py-2">Sin compras registradas</p>
                                      )}
                                    </ScrollArea>
                                  </div>

                                  {/* Formulations History */}
                                  <div className="space-y-2" data-testid="client-formulations-list">
                                    <h4 className="text-sm font-medium flex items-center gap-2 text-slate-500">
                                      <Beaker className="w-4 h-4" />
                                      Formulaciones ({clientHistory.formulations?.length || 0})
                                    </h4>
                                    <ScrollArea className="h-32">
                                      {clientHistory.formulations?.length > 0 ? (
                                        <div className="space-y-2 pr-4">
                                          {clientHistory.formulations.slice(0, 5).map((f, i) => (
                                            <div key={i} className="text-sm p-2 rounded bg-slate-50">
                                              <div className="flex items-center justify-between">
                                                <p className="font-medium text-slate-700 truncate">{f.name}</p>
                                                <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300 shrink-0 ml-2">
                                                  {f.ingredients?.length || 0} ing.
                                                </Badge>
                                              </div>
                                              <p className="text-xs text-slate-500">{formatDate(f.created_at)}</p>
                                              {f.notes && (
                                                <p className="text-xs text-slate-500 italic mt-1 line-clamp-2">{f.notes}</p>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-500 py-2">Sin formulaciones</p>
                                      )}
                                    </ScrollArea>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-slate-500 text-center py-4">No se pudo cargar el historial</p>
                              )}
                              
                              {/* Quick Actions */}
                              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="border-slate-200"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAuditModal(client, null);
                                  }}
                                  data-testid={`open-audit-${client.id}`}
                                >
                                  <History className="w-4 h-4 mr-2" />
                                  Ver historial completo
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedClient ? 'Editar cliente' : 'Nuevo cliente'}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6">
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Apellido *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                  placeholder="+52 55 1234 5678"
                />
              </div>
              <div className="space-y-2">
                <Label>Correo</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                  placeholder="correo@ejemplo.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cumpleaños</Label>
              <Input
                type="date"
                value={formData.birthday}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                className="bg-slate-100 border-slate-200"
              />
            </div>

            <div className="space-y-2">
              <Label>Etiquetas</Label>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Nueva etiqueta"
                  className="bg-slate-100 border-slate-200"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button variant="outline" onClick={addTag} className="border-slate-200">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-slate-500 border-slate-300">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Preferencias, observaciones..."
                className="bg-slate-100 border-slate-200 resize-none"
                rows={3}
              />
              </div>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-primary text-white hover:bg-primary/90" data-testid="save-client-btn">
              {selectedClient ? 'Actualizar' : 'Crear cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Detalle del cliente</DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <Tabs defaultValue="info" className="flex-1">
              <TabsList className="bg-slate-100 border border-slate-200">
                <TabsTrigger value="info" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <User className="w-4 h-4 mr-2" />
                  Información
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <History className="w-4 h-4 mr-2" />
                  Historial
                </TabsTrigger>
                <TabsTrigger value="loyalty" className="data-[state=active]:bg-white data-[state=active]:text-black">
                  <Gift className="w-4 h-4 mr-2" />
                  Lealtad
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[400px] mt-4">
                <TabsContent value="info" className="space-y-4 pr-4">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
                    <Avatar className="h-16 w-16">
                      <AvatarFallback className="bg-slate-200 text-white text-xl">
                        {getInitials(selectedClient.first_name, selectedClient.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xl font-bold">{selectedClient.first_name} {selectedClient.last_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {selectedClient.tags.map(tag => (
                          <Badge key={tag} variant="outline" className="text-slate-500">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-slate-50">
                      <p className="text-slate-500 text-sm">Teléfono</p>
                      <p className="font-medium">{selectedClient.phone || 'No registrado'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <p className="text-slate-500 text-sm">Correo</p>
                      <p className="font-medium truncate">{selectedClient.email || 'No registrado'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <p className="text-slate-500 text-sm">Cumpleaños</p>
                      <p className="font-medium">{selectedClient.birthday || 'No registrado'}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50">
                      <p className="text-slate-500 text-sm">Cliente desde</p>
                      <p className="font-medium">{formatDate(selectedClient.created_at)}</p>
                    </div>
                  </div>

                  {selectedClient.notes && (
                    <div className="p-4 rounded-lg bg-slate-50">
                      <p className="text-slate-500 text-sm mb-2">Notas</p>
                      <p>{selectedClient.notes}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-slate-50 text-center">
                      <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                        {selectedClient.total_visits}
                      </p>
                      <p className="text-slate-500 text-sm">Visitas</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 text-center">
                      <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>
                        {formatCurrency(selectedClient.total_spent)}
                      </p>
                      <p className="text-slate-500 text-sm">Total gastado</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-50 text-center">
                      <p className="text-3xl font-bold text-amber-400" style={{ fontFamily: 'Manrope' }}>
                        {selectedClient.loyalty_points}
                      </p>
                      <p className="text-slate-500 text-sm">Puntos</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="history" className="space-y-4 pr-4">
                  {/* Visit summary */}
                  {clientHistory?.summary && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="history-summary">
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <p className="text-xs text-slate-500">Visitas</p>
                        <p className="font-bold text-lg">{clientHistory.summary.total_visits}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <p className="text-xs text-slate-500">Total gastado</p>
                        <p className="font-bold text-lg text-emerald-600">
                          {formatCurrency(clientHistory.summary.total_spent)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <p className="text-xs text-slate-500">Propinas</p>
                        <p className="font-bold text-lg text-amber-600">
                          {formatCurrency(clientHistory.summary.total_tips)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <p className="text-xs text-slate-500">Última visita</p>
                        <p className="font-medium text-sm">
                          {clientHistory.summary.last_visit
                            ? formatDate(clientHistory.summary.last_visit)
                            : '—'}
                        </p>
                      </div>
                    </div>
                  )}

                  {clientHistory?.sales?.length > 0 ? (
                    <div className="space-y-3">
                      {clientHistory.sales.map(sale => (
                        <div
                          key={sale.id}
                          className="p-4 rounded-lg bg-slate-50 border border-slate-200"
                          data-testid={`visit-row-${sale.id}`}
                        >
                          <div className="flex items-start justify-between mb-2 gap-3">
                            <div>
                              <p className="text-xs text-slate-400 font-mono">{sale.ticket_number}</p>
                              <p className="text-sm font-medium text-slate-700">
                                {formatDate(sale.created_at, { year: 'numeric', month: 'short', day: '2-digit' })}
                              </p>
                              {sale.appointment_id ? (
                                <span
                                  className="inline-block mt-1 bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] px-1.5 py-0.5 rounded font-medium"
                                  data-testid={`history-visit-type-${sale.id}`}
                                >
                                  Con cita
                                </span>
                              ) : (
                                <span
                                  className="inline-block mt-1 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] px-1.5 py-0.5 rounded font-medium"
                                  data-testid={`history-visit-type-${sale.id}`}
                                >
                                  Walk-in
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{formatCurrency(sale.total)}</p>
                              <p className="text-[10px] text-slate-400 capitalize">
                                {sale.payment_method}
                                {sale.tip_amount > 0 && (
                                  <span className="text-amber-600 ml-1">
                                    + propina {formatCurrency(sale.tip_amount)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1 border-t border-slate-200 pt-2 mt-2">
                            {sale.items.map((item, i) => (
                              <div key={i} className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">
                                  {item.quantity}× {item.name}
                                  {item.barber_name && (
                                    <span className="text-xs text-slate-400 ml-2">
                                      · {item.barber_name}
                                    </span>
                                  )}
                                </span>
                                <span className="text-slate-500">{formatCurrency(item.subtotal)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Sin historial de visitas</p>
                      <p className="text-xs mt-1">Cada cobro registrado en POS aparecerá aquí</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="loyalty" className="space-y-4 pr-4">
                  <div className="p-6 rounded-lg bg-slate-50 text-center">
                    <Gift className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                    <p className="text-4xl font-bold text-amber-400" style={{ fontFamily: 'Manrope' }}>
                      {selectedClient.loyalty_points}
                    </p>
                    <p className="text-slate-500 mt-1">Puntos acumulados</p>
                    <Badge variant="outline" className={cn("mt-2", getLoyaltyLevel(selectedClient.loyalty_points).color)}>
                      <Star className="w-3 h-3 mr-1" />
                      Nivel {getLoyaltyLevel(selectedClient.loyalty_points).label}
                    </Badge>
                  </div>

                  {clientHistory?.loyalty_transactions?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-slate-500">Movimientos</h4>
                      {clientHistory.loyalty_transactions.map(tx => (
                        <div key={tx.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <div>
                            <p className="text-sm">{tx.description}</p>
                            <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                          </div>
                          <span className={cn(
                            "font-bold",
                            tx.points > 0 ? "text-emerald-400" : "text-red-400"
                          )}>
                            {tx.points > 0 ? '+' : ''}{tx.points}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Full audit history modal */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="!max-w-4xl bg-white border-slate-200 p-0" data-testid="audit-modal">
          <DialogHeader className="p-6 pb-3 border-b border-slate-100">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Historial de Citas de {auditClient?.first_name} {auditClient?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-slate-500">Desde</Label>
                <Input
                  type="date" value={auditFrom} onChange={(e) => setAuditFrom(e.target.value)}
                  className="h-9" data-testid="audit-from"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Hasta</Label>
                <Input
                  type="date" value={auditTo} onChange={(e) => setAuditTo(e.target.value)}
                  className="h-9" data-testid="audit-to"
                />
              </div>
              {auditFilterAppt && (
                <Badge
                  variant="outline"
                  className="text-xs cursor-pointer hover:bg-slate-100 border-emerald-300 text-emerald-700"
                  onClick={() => setAuditFilterAppt(null)}
                  data-testid="clear-appt-filter"
                >
                  Filtrando por 1 cita · <X className="w-3 h-3 ml-1 inline" />
                </Badge>
              )}
              <span className="text-xs text-slate-400 ml-auto">
                {auditLoading ? 'Cargando…' : `${auditEvents.length} evento(s)`}
              </span>
            </div>

            <div className="max-h-[60vh] overflow-y-auto scrollbar-visible border border-slate-200 rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50 z-10">
                  <TableRow>
                    <TableHead>Servicio / Bloque</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Empleado</TableHead>
                    <TableHead className="text-right">Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLoading ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></TableCell></TableRow>
                  ) : auditEvents.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">Sin eventos en el rango seleccionado.</TableCell></TableRow>
                  ) : auditEvents.map((e, idx) => {
                    const actionCfg = {
                      created: { label: 'Crear', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                      moved: { label: 'Mover', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                      resized: { label: 'Cambiar tamaño', cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
                      status_changed: { label: 'Cambiar estado', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
                      edited: { label: 'Editar', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                      updated: { label: 'Editar', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                      cancelled: { label: 'Cancelar', cls: 'bg-red-100 text-red-700 border-red-200' },
                      deleted: { label: 'Eliminar', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
                    }[e.action] || { label: e.action, cls: 'bg-slate-100 text-slate-700' };
                    const roleLabel = {
                      master_admin: 'Administrador maestro', admin: 'Administrador',
                      barber: 'Barbero', stylist: 'Estilista', client: 'Cliente',
                      receptionist: 'Recepcionista',
                    }[e.actor_role] || e.actor_role;
                    return (
                      <TableRow key={e.id || idx} data-testid={`audit-row-${idx}`}>
                        <TableCell>
                          <div className="text-sm font-medium text-slate-800">
                            {(e.service_names && e.service_names.length > 0) ? e.service_names.join(', ') : 'Sin servicios'}
                          </div>
                          <div className="text-xs text-slate-400">
                            {e.appointment_date} {e.appointment_start_time}–{e.appointment_end_time}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('text-xs border', actionCfg.cls)}>
                            {actionCfg.label}
                            {e._legacy && <span className="ml-1 text-[9px] opacity-60">LEGACY</span>}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-700">{e.actor_name}</div>
                          {roleLabel && <div className="text-xs text-slate-400">{roleLabel}</div>}
                        </TableCell>
                        <TableCell className="text-right text-xs text-slate-500 font-mono">
                          {new Date(e.at).toLocaleString('es-MX', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
                            timeZone: 'America/Mexico_City',
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
